import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { v4 as uuidv4 } from 'uuid';
import { ADD_TO_CART, IS_ADDING_TO_CART, PRODUCT_STATE_CHANGE } from '../../../events';
import { getSite } from '../../../utils';
import AddAlertButton from './buttons/AddAlertButton';
import AddToBasketButton from './buttons/AddToBasketButton';
import AvailableSoonButton from './buttons/AvailableSoonButton';
import LastMinuteShoppingButton from './buttons/LastMinuteShoppingButton';
import LastCall from './messages/LastCall';
/**
 * Each button is linked with product hash, each time a new variant or item is selected, an event is dispatched with the selected hash.
 * Buttons are listening to this event to update accordingly.
 */
export const ProductCta = ({ onlyBasketCta = false, allowAddedStatus, product, inDialogContext }) => {
    const size = !inDialogContext && window.location.hash.replace('#size-', '');
    // Local scoped uniq id to target buttons with custom events
    const [idButton] = useState(uuidv4());
    const [selectedVariant, setSelectedVariant] = useState(document.body.dataset.variant && !inDialogContext
        ? product.variants.find((variant) => variant.id === Number(document.body.dataset.variant))
        : product.selectedVariant);
    let defaultSelectedItem = selectedVariant.selectedItem;
    if (size) {
        const selectedItem = selectedVariant.items.find((item) => item.size === size);
        if (selectedItem) {
            defaultSelectedItem = selectedItem;
        }
    }
    const [selectedItem, setSelectedItem] = useState(defaultSelectedItem);
    const [addedToCart, setAddedToCart] = useState(false);
    const [isLoadingId, setIsLoadingId] = useState(undefined);
    // Listen to product added to cart
    useEffect(() => {
        const onProductAddedToCart = (e) => {
            if (allowAddedStatus && e.detail.ean === selectedItem.ean) {
                setAddedToCart(true);
            }
        };
        document.addEventListener(ADD_TO_CART, onProductAddedToCart);
        return () => {
            document.removeEventListener(ADD_TO_CART, onProductAddedToCart);
        };
    }, [selectedItem]);
    useEffect(() => {
        const isAddingProductToCart = (e) => {
            if (e.detail.id.includes(idButton)) {
                setIsLoadingId(e.detail.loading ? e.detail.id : undefined);
            }
        };
        document.addEventListener(IS_ADDING_TO_CART, isAddingProductToCart);
        return () => {
            document.removeEventListener(IS_ADDING_TO_CART, isAddingProductToCart);
        };
    }, [selectedItem]);
    // Listen to product state change (new variant or new item selected)
    useEffect(() => {
        if (inDialogContext) {
            const onProductStateChange = (e) => {
                const data = e.detail;
                if (data.productHash === product.hash) {
                    if (data.currentVariantHash) {
                        const variant = product.variants.find((productVariant) => productVariant.hash === data.currentVariantHash);
                        if (variant) {
                            setSelectedVariant(variant);
                            setSelectedItem(data.currentItemHash
                                ? variant.items.find((item) => item.hash === data.currentItemHash)
                                : variant.selectedItem);
                        }
                    }
                    else if (data.currentItemHash) {
                        const newSelectedItem = selectedVariant.items.find((item) => item.hash === data.currentItemHash);
                        if (newSelectedItem) {
                            setSelectedItem(newSelectedItem);
                        }
                    }
                }
            };
            document.addEventListener(PRODUCT_STATE_CHANGE, onProductStateChange);
            return () => {
                document.removeEventListener(PRODUCT_STATE_CHANGE, onProductStateChange);
            };
        }
    }, []);
    // Listen to size change (product page)
    useEffect(() => {
        if (!inDialogContext) {
            const onPopState = () => {
                const hashSize = decodeURIComponent(window.location.hash.replace('#size-', ''));
                if (selectedItem && hashSize !== selectedItem.size) {
                    setSelectedItem(selectedVariant.items.find((item) => item.size === hashSize));
                }
            };
            window.addEventListener('popstate', onPopState);
            return () => {
                window.removeEventListener('popstate', onPopState);
            };
        }
    }, [selectedItem]);
    if (!selectedVariant || !selectedItem) {
        return null;
    }
    const siteCode = getSite();
    const isPreorder = selectedVariant.preorder;
    const isAvailableSoon = selectedVariant.availableSoon;
    const isCustomisation = !!selectedVariant.customisation;
    const isAvailable = selectedItem.available;
    const isInOtherBasket = selectedItem.inOtherBasket;
    const lastMinuteShoppingAvailable = selectedItem.lastMinuteShoppingAvailable && siteCode?.startsWith('fr');
    const isCrossSellPopin = selectedVariant.isCrossSellPopin;
    const isCrossSellProductPopin = selectedVariant.isCrossSellProductPopin;
    const selectedVariantTracking = selectedVariant.tracking;
    const isLastCall = selectedItem.isLastCall && isAvailable && !isInOtherBasket && !onlyBasketCta;
    const tracking = {
        brand: selectedVariantTracking.brand,
        category: selectedVariantTracking.category,
        ean: selectedItem.ean,
        name: selectedVariantTracking.name,
        price: selectedVariantTracking.productPrice,
        variantColor: selectedVariantTracking.variantColor,
        content_ids: selectedVariantTracking.legacyId,
        productUUID: selectedVariantTracking.productUUID,
        sizeUUID: selectedItem.uuid,
        legacyId: selectedVariantTracking.legacyId,
        productBrand: selectedVariantTracking.productBrand,
        productCategory: selectedVariantTracking.productCategory,
        productEAN: selectedVariantTracking.productEAN,
        productID: selectedVariantTracking.productID,
        productName: selectedVariantTracking.productName,
        productPrice: selectedVariantTracking.productPrice,
        productStatus: selectedVariantTracking.productStatus,
        productURL: selectedVariantTracking.productURL,
        productVariant: selectedVariantTracking.productVariant,
        fsColor: selectedVariantTracking.fsColor,
        color: selectedVariantTracking.color,
        collection: selectedVariantTracking.collection,
    };
    let mainCta = _jsx(AddAlertButton, { soon: isAvailableSoon, tracking: tracking });
    if (isAvailableSoon) {
        mainCta = _jsx(AvailableSoonButton, {});
    }
    else if (isAvailable || isInOtherBasket) {
        mainCta = (_jsx(AddToBasketButton, { isAvailable: isAvailable, isPreorder: isPreorder, isInOtherBasket: isInOtherBasket, isCustomisation: isCustomisation, preorderDate: selectedVariant.preorderDate, preorderDateEnd: selectedVariant?.preorderDateEnd?.date
                ? new Date(selectedVariant.preorderDateEnd.date + selectedVariant.preorderDateEnd.timezone)
                : null, tracking: tracking, withTooltip: !onlyBasketCta, popinCrossSellPath: (isCrossSellPopin || isCrossSellProductPopin) && selectedVariant.link, popinCrossSellProductEnabled: isCrossSellProductPopin, ean: selectedItem.ean, addedToCart: addedToCart, isLoading: isLoadingId === idButton, id: idButton }));
    }
    if (onlyBasketCta || addedToCart) {
        return mainCta;
    }
    const lastMinuteIdButton = `${idButton}-lms`;
    const isLastMinuteLoading = isLoadingId === lastMinuteIdButton;
    return (_jsxs("div", { children: [!isAvailable && !isPreorder && !isInOtherBasket ? (_jsxs(_Fragment, { children: [lastMinuteShoppingAvailable && (_jsx(LastMinuteShoppingButton, { id: lastMinuteIdButton, className: "u-mb-sm", tracking: tracking, isLoading: isLastMinuteLoading })), mainCta] })) : (_jsxs(_Fragment, { children: [mainCta, lastMinuteShoppingAvailable && (_jsx(LastMinuteShoppingButton, { id: lastMinuteIdButton, color: "secondary", className: "u-mt-sm", tracking: tracking, isLoading: isLastMinuteLoading }))] })), isLastCall && _jsx(LastCall, {})] }));
};
let ctaReactRoots = [];
// clear to avoid unlinked instances in the wild and updates conflicts
export const clearCtas = () => {
    ctaReactRoots.forEach((root) => {
        try {
            root.unmount();
        }
        catch (error) {
            console.warn('Error while unmounting root:', error);
        }
    });
    ctaReactRoots = [];
};
export const renderProductCta = (config) => {
    const { container = document, inDialogContext = false } = config || {};
    if (container === document) {
        clearCtas();
    }
    container.querySelectorAll('[data-product-cta]').forEach((ctaContainer) => {
        const onlyBasketCta = ctaContainer.hasAttribute('data-only-basket-cta');
        const allowAddedStatus = ctaContainer.hasAttribute('data-allow-added-status');
        // The product state is either on the button (ie in modals) or global (product page)
        const product = ctaContainer.dataset.product
            ? JSON.parse(ctaContainer.dataset.product)
            : window.product;
        const root = createRoot(ctaContainer);
        root.render(_jsx(ProductCta, { onlyBasketCta: onlyBasketCta, allowAddedStatus: allowAddedStatus, product: product, inDialogContext: inDialogContext }));
        ctaReactRoots.push(root);
    });
};
